import { createFileRoute, Link, notFound, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { zodValidator } from "@tanstack/zod-adapter";
import { findCityBySlug, cityIntro, photographerMatchesCity, type CityInfo } from "@/data/cities";
import { themes, priceBuckets, type Photographer, type Theme } from "@/data/photographers";
import { fetchAllPhotographers } from "@/lib/photographers-source";
import { PortraitImage } from "@/components/PortraitImage";
import { CreatorFilterBar } from "@/components/CreatorFilterBar";
import { creatorFiltersSchema, matchesCreatorFilters, type CreatorFilters } from "@/lib/creator-filters";

const PAGE_SIZE = 12;

export const Route = createFileRoute("/fotograf/$city/$theme")({
  validateSearch: zodValidator(creatorFiltersSchema),
  loader: ({ params }) => {
    const city = findCityBySlug(params.city);
    const theme = themes.find((t) => t.id === params.theme);
    if (!city || !theme) throw notFound();
    return { city, theme };
  },
  head: ({ loaderData, params }) => {
    const city = loaderData?.city;
    const theme = loaderData?.theme;
    if (!city || !theme) return { meta: [{ title: "Fotograf, OM Selected" }] };
    const title = `${theme.label}fotograf ${city.name}, OM Selected`;
    const description = `Kuratierte ${theme.label}-Fotografen in ${city.name} (${city.country}). ${theme.description} Redaktionell empfohlen von OM Selected.`;
    const url = `https://omselected.art/fotograf/${params.city}/${params.theme}`;
    const ogImage = `https://omselected.art/og/fotograf/${params.city}/${params.theme}.png`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { property: "og:image", content: ogImage },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { property: "og:image:type", content: "image/png" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:image", content: ogImage },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: title,
            description,
            url,
            about: {
              "@type": "Place",
              name: city.name,
              address: { "@type": "PostalAddress", addressLocality: city.name, addressCountry: city.country },
            },
            isPartOf: { "@type": "WebSite", name: "OM Selected", url: "https://omselected.art" },
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <div className="mx-auto max-w-[800px] px-5 py-32 text-center">
      <h1 className="serif text-4xl">Seite nicht gefunden</h1>
      <Link to="/photographers" className="eyebrow inline-block mt-8 border-b border-foreground pb-1">
        Alle Fotografen ansehen
      </Link>
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="mx-auto max-w-[800px] px-5 py-32 text-center">
      <h1 className="serif text-4xl">Fehler</h1>
      <p className="mt-4 text-muted-foreground">{error.message}</p>
    </div>
  ),
  component: CityThemePage,
});

function CityThemePage() {
  const { t } = useTranslation();
  const { city, theme } = Route.useLoaderData() as { city: CityInfo; theme: { id: Theme; label: string; description: string } };
  const search = Route.useSearch();
  const { page } = search;
  const navigate = useNavigate({ from: Route.fullPath });
  const [all, setAll] = useState<Photographer[]>([]);
  const [loading, setLoading] = useState(true);

  const updateFilters = (patch: Partial<CreatorFilters>) =>
    navigate({ search: (prev: CreatorFilters) => ({ ...prev, ...patch, page: 1 }), replace: true });
  const resetFilters = () =>
    navigate({
      search: { country: "", city: "", theme: "", price: "", setting: "", language: "", page: 1 },
      replace: true,
    });

  useEffect(() => {
    let active = true;
    fetchAllPhotographers().then((data) => {
      if (active) {
        setAll(data);
        setLoading(false);
      }
    });
    return () => { active = false; };
  }, []);

  const matching = useMemo(
    () =>
      all.filter(
        (p) =>
          (photographerMatchesCity(p.city, city.name) || photographerMatchesCity(p.region, city.name)) &&
          p.themes.includes(theme.id) &&
          matchesCreatorFilters(p, search, priceBuckets),
      ),
    [all, city.name, theme.id, search],
  );

  const totalPages = Math.max(1, Math.ceil(matching.length / PAGE_SIZE));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const pageItems = matching.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const otherThemes = themes.filter((t) => t.id !== theme.id);

  return (
    <div>
      <section className="border-b border-border">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 pt-12 pb-16">
          <div className="eyebrow text-muted-foreground">
            <Link to="/fotograf/$city" params={{ city: city.slug }} className="hover:text-foreground">
              Fotograf {city.name}
            </Link>
            {" · "}
            {theme.label}
          </div>
          <h1 className="serif text-5xl md:text-7xl mt-6 leading-[0.95]">
            {theme.label}fotograf in {city.name}
          </h1>
          <p className="serif italic text-xl md:text-2xl mt-8 max-w-2xl">
            {theme.description} Kuratierte Auswahl für {city.name} und Umgebung.
          </p>
          <div className="mt-8 flex flex-wrap gap-4 eyebrow text-muted-foreground">
            {otherThemes.map((t) => (
              <Link
                key={t.id}
                to="/fotograf/$city/$theme"
                params={{ city: city.slug, theme: t.id }}
                className="hover:text-foreground"
              >
                {t.label} in {city.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-border">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-16">
          <h2 className="eyebrow text-muted-foreground mb-8 font-normal">
            {theme.label}-Fotografen in {city.name}
          </h2>
          <CreatorFilterBar
            values={search}
            onChange={updateFilters}
            onReset={resetFilters}
            resultsCount={matching.length}
            show={{
              price: priceBuckets.map((b) => ({ value: b.id, label: b.label })),
              setting: true,
              language: true,
            }}
          />
          {loading ? (
            <div className="text-xs text-muted-foreground italic">{t("common.loading")}</div>
          ) : matching.length === 0 ? (
            <div className="space-y-6 max-w-xl">
              <p className="text-muted-foreground">
                Noch keine gelisteten {theme.label}-Fotografen in {city.name}. Sieh dir alle
                Fotografen in {city.name} an oder sende eine Anfrage, wir schlagen passende
                Profile vor.
              </p>
              <div className="flex flex-wrap gap-4 eyebrow">
                <Link to="/fotograf/$city" params={{ city: city.slug }} className="border-b border-foreground pb-1">
                  Alle Fotografen in {city.name}
                </Link>
                <Link to="/brief" className="border-b border-foreground pb-1">
                  Anfrage senden
                </Link>
              </div>
            </div>
          ) : (
            <>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
                {pageItems.map((p) => (
                  <Link key={p.id} to="/photographers/$id" params={{ id: p.id }} className="block group cursor-pointer">
                    <PortraitImage p={p} />
                    <div className="mt-4 flex justify-between items-baseline">
                      <h3 className="serif text-2xl">{p.name}</h3>
                      <div className="eyebrow text-muted-foreground">{p.city ?? p.region}</div>
                    </div>
                    <div className="mt-2 eyebrow text-muted-foreground">
                      {p.themes.map((t) => themes.find((th) => th.id === t)?.label).join(" · ")}
                    </div>
                  </Link>
                ))}
              </div>
              {totalPages > 1 && (
                <nav
                  aria-label="Seitennavigation"
                  className="mt-16 flex items-center justify-between gap-4 eyebrow text-muted-foreground"
                >
                  {safePage > 1 ? (
                    <Link
                      to="/fotograf/$city/$theme"
                      params={{ city: city.slug, theme: theme.id }}
                      search={(prev: CreatorFilters) => ({ ...prev, page: safePage - 1 })}
                      className="border-b border-foreground pb-1 hover:text-foreground"
                    >
                      {t("common.prev")}
                    </Link>
                  ) : (
                    <span className="opacity-30">{t("common.prev")}</span>
                  )}
                  <span>
                    {t("common.pageOf", { page: safePage, total: totalPages })}
                  </span>
                  {safePage < totalPages ? (
                    <Link
                      to="/fotograf/$city/$theme"
                      params={{ city: city.slug, theme: theme.id }}
                      search={(prev: CreatorFilters) => ({ ...prev, page: safePage + 1 })}
                      className="border-b border-foreground pb-1 hover:text-foreground"
                    >
                      {t("common.next")}
                    </Link>
                  ) : (
                    <span className="opacity-30">{t("common.next")}</span>
                  )}
                </nav>
              )}
            </>
          )}
        </div>
      </section>

      <section>
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-16">
          <p className="serif italic text-lg max-w-2xl text-muted-foreground">
            {cityIntro(city)}
          </p>
        </div>
      </section>
    </div>
  );
}